import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../../hooks/useNotifications';
import { getAdminPrefix } from '../../constants';
import { useToast } from '../ui/Toast';
import { formatDate } from '../../utils/formatPreferences';
import './NotificationDropdown.css';

/**
 * Notification Dropdown Component
 * Displays notifications with navigation to relevant pages
 */
const NotificationDropdown = ({ isOpen, onClose }) => {
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { notifications, unreadCount, loading, markAsRead, markAllAsRead, deleteNotif } = useNotifications(10, true);
  const [position, setPosition] = useState({ top: 0, right: 0 });

  // Calculate dropdown position based on notification button
  useEffect(() => {
    if (isOpen) {
      const notificationBtn = document.querySelector('.notification-btn');
      if (notificationBtn) {
        const rect = notificationBtn.getBoundingClientRect();
        setPosition({
          top: rect.bottom + 8, // 8px below the button
          right: window.innerWidth - rect.right
        });
      }
    }
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        // Check if click is not on the notification button
        if (!event.target.closest('.notification-btn')) {
          onClose();
        }
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  const handleNotificationClick = async (notification) => {
    // Mark as read if unread
    if (!notification.read) {
      try {
        await markAsRead(notification.id);
      } catch (error) {
        console.error('Error marking notification as read:', error);
      }
    }

    // Navigate if action is provided
    if (notification.action && notification.action.type === 'route') {
      let route = notification.action.path;
      
      // If no path specified, default based on section
      if (!route) {
        const prefix = getAdminPrefix();
        if (notification.action.section === 'notifications' || notification.category === 'notifications') {
          route = `${prefix}/analytics`;
        } else if (notification.category === 'users') {
          route = `${prefix}/users`;
        } else if (notification.category === 'waitlist') {
          route = `${prefix}/waitlist`;
        } else {
          route = `${prefix}/dashboard`;
        }
      }
      
      navigate(route);
      onClose();
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await markAllAsRead();
      showToast('All notifications marked as read', 'success');
    } catch (error) {
      showToast('Error marking notifications as read', 'error');
    }
  };

  const handleDelete = async (e, notificationId) => {
    e.stopPropagation();
    try {
      await deleteNotif(notificationId);
      showToast('Notification deleted', 'success');
    } catch (error) {
      showToast('Error deleting notification', 'error');
    }
  };

  const formatTime = (createdAt) => {
    if (!createdAt) return 'Just now';
    
    const now = new Date();
    const notifDate = new Date(createdAt);
    const diffMs = now - notifDate;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    
    // Use user preference for date formatting
    return formatDate(notifDate);
  };

  if (!isOpen) return null;

  return (
    <div 
      className="notification-dropdown" 
      ref={dropdownRef}
      style={{
        top: `${position.top}px`,
        right: `${position.right}px`
      }}
    >
      <div className="notification-dropdown-header">
        <div className="header-left">
          <h3>Notifications</h3>
          {unreadCount > 0 && (
            <span className="unread-badge">{unreadCount} unread</span>
          )}
        </div>
        <div className="header-actions">
          {unreadCount > 0 && (
            <button 
              className="mark-all-read-btn" 
              onClick={handleMarkAllAsRead}
              aria-label="Mark all as read"
              title="Mark all as read"
            >
              <i className="bi bi-check-all"></i>
            </button>
          )}
          <button 
            className="close-btn" 
            onClick={onClose}
            aria-label="Close notifications"
          >
            <i className="bi bi-x"></i>
          </button>
        </div>
      </div>

      <div className="notification-dropdown-content">
        {loading ? (
          <div className="notification-loading">
            <div className="loading-spinner"></div>
            <p>Loading notifications...</p>
          </div>
        ) : notifications.length === 0 ? (
          <div className="notification-empty">
            <i className="bi bi-bell-slash icon-brand"></i>
            <p>No notifications</p>
            <span>All systems running smoothly</span>
          </div>
        ) : (
          notifications.map((notification) => (
            <div
              key={notification.id}
              className={`notification-item notification-${notification.type} ${notification.read ? 'read' : 'unread'}`}
              onClick={() => handleNotificationClick(notification)}
            >
              <div className="notification-icon">
                <i className={`bi ${
                  notification.type === 'error' ? 'bi-exclamation-circle' :
                  notification.type === 'warning' ? 'bi-exclamation-triangle' :
                  notification.type === 'success' ? 'bi-check-circle' :
                  'bi-info-circle'
                } icon-brand`}></i>
                {!notification.read && <span className="unread-dot"></span>}
              </div>
              <div className="notification-content">
                <div className="notification-header">
                  <h4>{notification.title}</h4>
                  {notification.priority === 'urgent' && (
                    <span className="priority-badge urgent">Urgent</span>
                  )}
                  {notification.priority === 'high' && (
                    <span className="priority-badge high">High</span>
                  )}
                </div>
                <p>{notification.message}</p>
                <span className="notification-time">{formatTime(notification.created_at || notification.createdAt)}</span>
              </div>
              <div className="notification-actions">
                {notification.action && (
                  <div className="notification-arrow">
                    <i className="bi bi-chevron-right"></i>
                  </div>
                )}
                <button
                  className="delete-notification-btn"
                  onClick={(e) => handleDelete(e, notification.id)}
                  aria-label="Delete notification"
                  title="Delete"
                >
                  <i className="bi bi-x"></i>
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {notifications.length > 0 && (
        <div className="notification-dropdown-footer">
          <button 
            className="view-all-btn"
            onClick={() => {
              navigate(`${getAdminPrefix()}/analytics`);
              onClose();
            }}
          >
            View All Analytics
          </button>
        </div>
      )}
    </div>
  );
};

export default NotificationDropdown;

